import {chain} from 'lodash';
import React, {FC, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {intersperseNodes} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CompanyModel} from '../../../../libs/shared/core/src/models/api/company/companyModel';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {HighlightTypesEnum} from '../../../models/api/highlightBaseModel';
import {SearchTagModel} from '../../../models/api/searchTagModel';
import {TagModel} from '../../../models/api/tagModel';
import {BaseFilterDropdownProps} from '../../common/filters/common/types';
import {ModalFilterDropdown} from '../../common/filters/modalFilter/modalFilterDropdown';
import {FilterRowGenerator} from '../../common/filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceAsync} from '../../common/filters/sourceFilter/filterDropdownSource';
import {ResourceMultiSelectInput} from '../../common/resourceMultiSelectInput/resourceMultiSelectInput';
import {
  buildFetcher,
  buildRowData,
  StyledTagIcon,
  StyledTagTitle,
  StyledTagWrapper,
} from './tagFilterHelpers';
import {LensFormMultiselectProps} from './types';

/*
 * Props.
 */

interface LensFormExcludeTagsModalFilterDropdownProps
  extends Omit<LensFormMultiselectProps<TagModel>, 'choices'>,
    BaseFilterDropdownProps {
  namespace: string | undefined;
  inboxIds: ReadonlyArray<number>;
  noTags: boolean;
  onSetNoTags: (arg: boolean) => void;
  initialSelectedTags: ReadonlyArray<SearchTagModel>;
  tagsMap: TagsMap;
  setTagsMap: (map: TagsMap) => void;
  company: CompanyModel;
  idsToDisable: ReadonlyArray<number>;
}

/*
 * Style.
 */

export const StyledValueWrapper = styled.div`
  display: flex;
  gap: 4px;
  ${ellipsis()}
`;

/*
 * Types.
 */

export interface TagsMap {
  [key: number]: TagModel;
}

/*
 * Component.
 */

export const LensFormExcludeTagsModalFilterDropdown: FC<LensFormExcludeTagsModalFilterDropdownProps> = (
  props,
) => {
  const {
    selectedIds,
    onSetItemIds,
    namespace,
    inboxIds,
    noTags,
    onSetNoTags,
    initialSelectedTags,
    company,
    tagsMap,
    setTagsMap,
    idsToDisable,
  } = props;
  const dispatch = useAppDispatch();
  const [localSelectedIds, setLocalSelectedIds] = useState<ReadonlyArray<number>>(selectedIds);
  const [localNoTags, setLocalNoTags] = useState<boolean>(noTags);
  const theme = useUserTheme();

  const dropdownSource = useMemo(() => {
    const fetcher = buildFetcher(company, namespace, initialSelectedTags, inboxIds);
    return computeFilterDropdownSourceAsync(fetcher);
  }, [company, inboxIds, initialSelectedTags, namespace]);

  const buildRowGenerator: FilterRowGenerator<SearchTagModel> = useCallback(
    (searchTag, dropdownSelectedIds, makeOnItemSelect, isSearching, initialSelectedIds) =>
      buildRowData(
        searchTag,
        dropdownSelectedIds,
        makeOnItemSelect,
        isSearching,
        initialSelectedIds,
        setTagsMap,
        tagsMap,
        dispatch,
        idsToDisable,
      ),
    [dispatch, setTagsMap, tagsMap, idsToDisable],
  );

  const renderTagSelectionDropdown = () => (
    <ResourceMultiSelectInput
      dropdownSource={dropdownSource}
      selectedItemIds={localSelectedIds}
      computeFilterGenerator={buildRowGenerator}
      onSetItems={setLocalSelectedIds}
      onClearAll={() => setLocalSelectedIds([])}
      renderValue={() => renderTagSelectionValue(localSelectedIds, tagsMap, theme)}
      renderPlaceholder={() => (
        <Trans id="lens-form-multiselect-exclude-tags-placeholder">Select shared tags</Trans>
      )}
      dropdownTitle={<Trans id="lens-form-multiselect-exclude-tags-dropdown-title">Shared tags</Trans>}
      isDisabled={localNoTags}
      isRequired={false}
      messageForDisabled={
        <Trans id="lens-form-multiselect-missing-inbox-message">Currently excluding all tags.</Trans>
      }
      hasError={props.hasError}
      errorMessage={props.errorMessage}
    />
  );

  const onApplyClicked = () => {
    onSetItemIds(localSelectedIds);
    onSetNoTags(localNoTags);
  };
  const onCancelClicked = () => {};
  const onClearClicked = () => {
    setLocalSelectedIds([]);
    setLocalNoTags(false);
  };

  return (
    <ModalFilterDropdown
      anchor={props.anchor}
      onRequestClose={props.onRequestClose}
      title={<Trans id="lens-form-exclude-tags-filter-modal-title">Exclude shared tags</Trans>}
      filters={[
        {
          value: <Trans id="lens-form-exclude-tags-filter-modal-doesnt-include">Doesn&apos;t include</Trans>,
          id: 'analytics-doesnt-include-tags',
          isChecked: !localNoTags,
          onSelect: () => {
            setLocalNoTags(false);
          },
          renderDropdown: renderTagSelectionDropdown,
        },
        {
          value: (
            <Trans id="lens-form-exclude-tags-filter-modal-doesnt-have-any-tags">
              Doesn&apos;t have any tags
            </Trans>
          ),
          id: 'analytics-doesnt-have-any-tags',
          isChecked: localNoTags,
          onSelect: () => {
            setLocalNoTags(true);
            setLocalSelectedIds([]);
          },
        },
      ]}
      footerHandlers={{onApplyClicked, onCancelClicked}}
      onClear={onClearClicked}
      isActive
      shouldCloseOnClear={false}
      clearButtonLabel={<Trans id="lens-form-exclude-tags-filter-modal-clear">Clear all</Trans>}
    />
  );
};

/*
 * Helpers.
 */

export function renderTagSelectionValue(
  selectedIds: ReadonlyArray<number>,
  tagsMap: TagsMap,
  theme: UserTheme,
) {
  if (!selectedIds.length) {
    return null;
  }

  const selectedTags = chain(selectedIds)
    .map((id) => tagsMap[id])
    .compact()
    .value();

  return (
    <StyledValueWrapper>
      {intersperseNodes(
        selectedTags.map((t) => {
          const tagColor =
            t.type === HighlightTypesEnum.COLOR
              ? theme.palette[t.highlightColor].shade40
              : theme.greys.shade60;

          return {
            node: (
              <StyledTagWrapper key={`tag-${t.id}`}>
                <StyledTagIcon>{<Icon name="noTagSmall" color={tagColor} />}</StyledTagIcon>
                <StyledTagTitle>{t.name}</StyledTagTitle>
              </StyledTagWrapper>
            ),
            key: String(t.id),
          };
        }),
        () => ', ',
      )}
    </StyledValueWrapper>
  );
}
